import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import 'react-slideshow-image/dist/styles.css';
import {
    PrimaryTitleGray,
    BrighterGreen,
    BlueTextDark,
    PrimaryTextColor,
} from '../../../../app/theme/tokens/colors';
import { PrimaryButton, Select } from '../../../../shared/ui/Inputs';
import { Panel } from '../../../../shared/ui/panel/panels';
import { getProductsImages } from '../../../../services/products/productService';
import { useOrderFlowStore } from '../../store/newOrderFlowStore';
import { getBase64Img } from '../../../../utils/format';
import { Spinner } from '../../../../shared/ui/Spinners';

const EXCLUDED_DESCRIPTIONS = ['Não se aplica', 'Cilindros'];

const getValidDescription = (value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized || normalized === '0') return '';
    return normalized;
};

const toRgba = (hexColor, alpha) => {
    const value = String(hexColor || '').replace('#', '');
    if (value.length !== 6) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TYPE_BADGE_COLORS = {
    equipamento: {
        bg: toRgba(BrighterGreen, 0.14),
        text: BrighterGreen,
        border: toRgba(BrighterGreen, 0.32),
    },
    cilindro: {
        bg: toRgba(BlueTextDark, 0.12),
        text: BlueTextDark,
        border: toRgba(BlueTextDark, 0.26),
    },
    descartavel: {
        bg: toRgba(PrimaryTitleGray, 0.06),
        text: PrimaryTitleGray,
        border: toRgba(PrimaryTitleGray, 0.22),
    },
    acessorios: {
        bg: toRgba(PrimaryTextColor, 0.14),
        text: PrimaryTextColor,
        border: toRgba(PrimaryTextColor, 0.3),
    },
};

const normalizeBadgeKey = (value = '') =>
    String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

const getSegmentStyle = (segment) => {
    const key = normalizeBadgeKey(segment);
    const mapped = TYPE_BADGE_COLORS[key];
    if (mapped) return mapped;

    if (key.includes('equip')) return TYPE_BADGE_COLORS.equipamento;
    if (key.includes('cilind')) return TYPE_BADGE_COLORS.cilindro;
    if (key.includes('descart')) return TYPE_BADGE_COLORS.descartavel;
    if (key.includes('acessor') || key.includes('acessori')) {
        return TYPE_BADGE_COLORS.acessorios;
    }

    return {
        bg: toRgba(PrimaryTextColor, 0.1),
        text: PrimaryTextColor,
        border: toRgba(PrimaryTextColor, 0.22),
    };
};

/**
 * @param {{
 *   currentUser: { canChooseEquipmentSubCateogry?: boolean },
 *   subcategory: object,
 *   onSelectProduct?: (product: import('../../../../types/product/Product').Product) => void,
 *   compact?: boolean,
 *   onDeselectProduct?: () => void,
 *   selectedProductId?: number
 * }} props
 */
export const SubCategoryCard = ({ currentUser, subcategory, onSelectProduct = undefined, selectedProductId = undefined, compact = false, onDeselectProduct = undefined }) => {
    const modelSelectId = useId();
    const setSelectedSubcategory = useOrderFlowStore(
        (state) => state.setSelectedSubcategory
    );
    const navigate = useNavigate();

    const firstProduct = subcategory.produtos.find((item) => item.id === selectedProductId) || subcategory.produtos[0];

    const [selectedModel, setSelectedModel] = useState(firstProduct.sap_code);
    const [image, setImage] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const descriptionRef = useRef(null);

    const [currentEquipment, setCurrentEquipment] = useState({
        name: firstProduct.description,
        shortDescription: getValidDescription(firstProduct.explanation1),
        technicalInfo: firstProduct.technical_information,
        segment: firstProduct.product_segment?.description || '',
        warning: '',
    });

    // Detecta se o texto está truncado após render e quando o conteúdo muda
    useEffect(() => {
        const el = descriptionRef.current;
        if (!el) return;
        setIsTruncated(el.scrollHeight > el.clientHeight);
    }, [currentEquipment.shortDescription]);

    const chooseModel = async (codProd) => {
        const selected = subcategory.produtos.find(
            (x) => String(x.sap_code) === String(codProd)
        );
        if (!selected) {
            setCurrentEquipment({
                name: subcategory.description,
                shortDescription: subcategory.description,
                technicalInfo: '',
                segment: '',
                warning: '',
            });
        } else {
            setCurrentEquipment({
                name: selected.description,
                shortDescription: getValidDescription(selected.explanation1),
                technicalInfo: selected.technical_information,
                segment: selected.product_segment?.description || '',
                warning: '',
            });
        }
        setSelectedModel(codProd);
        if (onSelectProduct && selected && subcategory.produtos.some((item) => item.id === selectedProductId)) {
            onSelectProduct(selected);
        }
    };

    useEffect(() => {
        let current = true;
        setIsLoading(true);
        setImage([]);
        getProductsImages([selectedModel])
            .then((images) => { if (current) setImage(images); })
            .catch(() => { if (current) setImage([]); })
            .finally(() => { if (current) setIsLoading(false); });
        return () => { current = false; };
    }, [selectedModel]);

    const handleButtonClick = async () => {
        if (onDeselectProduct) { onDeselectProduct(); return; }
        if (onSelectProduct) {
            const selected = subcategory.produtos.find((item) => String(item.sap_code) === String(selectedModel));
            if (selected) onSelectProduct(selected);
            return;
        }
        setIsButtonLoading(true);
        const model =
            selectedModel === '' || selectedModel === 'Model'
                ? null
                : parseInt(selectedModel, 10);
        const selected = subcategory.produtos.find(
            (x) => parseInt(x.sap_code) === parseInt(model)
        );
        await setSelectedSubcategory(selected || subcategory, model);
        setIsButtonLoading(false);
        navigate('/order/configureEquipment');
    };

    if (EXCLUDED_DESCRIPTIONS.includes(subcategory.description)) {
        return null;
    }

    const segmentStyle = getSegmentStyle(currentEquipment.segment);

    return (
        <>
            {isButtonLoading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9998,
                    }}
                >
                    <Spinner />
                </div>
            )}

            <Panel
                className="tile-hover"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: compact ? 344 : undefined,
                    flexShrink: 0,
                    border: compact ? '1px solid #dce5de' : undefined,
                    boxShadow: compact ? 'none' : undefined,
                    transition: 'box-shadow 0.2s ease',
                    outline: onSelectProduct && subcategory.produtos.some((item) => item.id === selectedProductId) ? `2px solid ${BrighterGreen}` : undefined,
                }}
            >
                {/* ── Cabeçalho ── */}
                <div
                    style={{
                        padding: compact ? '10px 12px' : '16px 20px 14px',
                        height: compact ? 78 : undefined,
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        borderBottom: '1px solid #f0f0f0',
                    }}
                >
                    {currentEquipment.segment && (
                        <span
                            style={{
                                display: 'inline-block',
                                marginBottom: compact ? '4px' : '8px',
                                padding: compact ? '1px 8px' : '2px 10px',
                                borderRadius: '20px',
                                border: `1px solid ${segmentStyle.border}`,
                                fontSize: compact ? '10px' : '11px',
                                fontWeight: '600',
                                letterSpacing: '0.4px',
                                backgroundColor: segmentStyle.bg,
                                color: segmentStyle.text,
                            }}
                        >
                            {currentEquipment.segment}
                        </span>
                    )}
                    <p
                        title={subcategory.description}
                        style={{
                            margin: 0,
                            fontWeight: 'bold',
                            fontSize: compact ? '13px' : '15px',
                            display: compact ? '-webkit-box' : undefined,
                            WebkitLineClamp: compact ? 1 : undefined,
                            WebkitBoxOrient: compact ? 'vertical' : undefined,
                            overflow: compact ? 'hidden' : undefined,
                            color: PrimaryTitleGray,
                            lineHeight: '1.3',
                        }}
                    >
                        {subcategory.description}
                    </p>
                </div>

                {/* ── Imagem ── */}
                <div
                    style={{
                        height: compact ? '80px' : '160px',
                        boxSizing: compact ? 'border-box' : undefined,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: compact ? '6px 12px' : '12px 24px',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #f0f0f0',
                    }}
                >
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <img
                            style={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                borderRadius: '6px',
                            }}
                            src={
                                image[0]
                                    ? getBase64Img(image[0])
                                    : '/icones/placeholder-image.png'
                            }
                            alt={currentEquipment.name}
                        />
                    )}
                </div>

                {/* ── Select de modelo ── */}
                {currentUser.canChooseEquipmentSubCateogry && (
                    <div
                        style={{ padding: compact ? '6px 12px' : '12px 20px', height: compact ? 56 : undefined, boxSizing: 'border-box', flexShrink: 0, borderBottom: '1px solid #f0f0f0' }}
                    >
                        <label
                            htmlFor={modelSelectId}
                            style={{
                                display: 'block',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#888',
                                letterSpacing: '0.5px',
                                marginBottom: '6px',
                                textTransform: 'uppercase',
                            }}
                        >
                            Modelo
                        </label>
                        <Select
                            id={modelSelectId}
                            value={selectedModel}
                            onChange={(e) => chooseModel(e.target.value)}
                            style={{ width: '100%', minWidth: 0, height: compact ? 28 : undefined, fontSize: compact ? 12 : undefined, textOverflow: 'ellipsis' }}
                        >
                            {subcategory.produtos.map((m, optIdx) => (
                                <option key={optIdx} value={m.sap_code}>
                                    {m.description}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* ── Descrição ── */}
                <div style={{ padding: compact ? '8px 12px' : '14px 20px 16px', flex: 1, minHeight: 0, overflow: compact ? 'hidden' : undefined }}>
                    <p
                        title={currentEquipment.name}
                        style={{
                            margin: '0 0 6px',
                            fontWeight: 'bold',
                            fontSize: compact ? '12px' : '13px',
                            display: compact ? '-webkit-box' : undefined,
                            WebkitLineClamp: compact ? 2 : undefined,
                            WebkitBoxOrient: compact ? 'vertical' : undefined,
                            overflow: compact ? 'hidden' : undefined,
                            color: PrimaryTitleGray,
                            lineHeight: '1.4',
                        }}
                    >
                        {currentEquipment.name}
                    </p>

                    {currentEquipment.shortDescription && (
                        <div style={{ position: 'relative' }}>
                            <Tooltip
                                title={compact && isTruncated ? currentEquipment.shortDescription : ''}
                                placement="top"
                                arrow
                                disableInteractive
                                enterDelay={0}
                                PopperProps={{ popperOptions: { strategy: 'fixed' } }}
                                componentsProps={{
                                    tooltip: { sx: { maxWidth: 'min(320px, calc(100vw - 32px))', bgcolor: '#1a1a1a', color: '#fff', fontSize: 12, lineHeight: 1.6, p: '8px 12px', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' } },
                                    arrow: { sx: { color: '#1a1a1a' } },
                                }}
                            >
                                <p
                                    ref={descriptionRef}
                                    onMouseEnter={() => {
                                        const el = descriptionRef.current;
                                        const truncated = Boolean(el && el.scrollHeight > el.clientHeight);
                                        setIsTruncated(truncated);
                                        if (!compact && truncated) setTooltipVisible(true);
                                    }}
                                    onMouseLeave={() => setTooltipVisible(false)}
                                    style={{
                                        margin: 0,
                                        fontSize: '12px',
                                        color: '#666',
                                        lineHeight: '1.6',
                                        display: '-webkit-box',
                                        WebkitLineClamp: compact ? 2 : 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {currentEquipment.shortDescription}
                                </p>

                            </Tooltip>

                            {!compact && tooltipVisible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: 'calc(100% + 8px)',
                                        left: '0',
                                        zIndex: 9999,
                                        backgroundColor: '#1a1a1a',
                                        color: '#fff',
                                        fontSize: '12px',
                                        lineHeight: '1.6',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        maxWidth: '280px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                                        pointerEvents: 'none',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {currentEquipment.shortDescription}
                                    {/* seta do tooltip */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '16px',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '6px solid transparent',
                                            borderRight: '6px solid transparent',
                                            borderTop: '6px solid #1a1a1a',
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Rodapé ── */}
                <div style={{ padding: compact ? '8px 12px' : '12px 20px', flexShrink: 0, borderTop: '1px solid #f0f0f0' }}>
                    <PrimaryButton
                        type="button"
                        aria-label={onDeselectProduct ? "Trocar produto" : onSelectProduct ? `Selecionar ${currentEquipment.name}` : undefined}
                        aria-pressed={onSelectProduct ? selectedProductId === subcategory.produtos.find((item) => String(item.sap_code) === String(selectedModel))?.id : undefined}
                        onClick={handleButtonClick}
                        style={{ width: '100%', height: compact ? '30px' : '36px', fontSize: compact ? '12px' : '13px' }}
                    >
                        {onDeselectProduct ? 'Trocar produto' : onSelectProduct ? (subcategory.produtos.some((item) => item.id === selectedProductId) ? 'Selecionado' : 'Selecionar') : 'Selecionar e continuar'}
                    </PrimaryButton>
                </div>
            </Panel>
        </>
    );
};
